import type { WorkspaceSlowApply } from '@/lib/apiTypes'
import { formatDateTime, formatDuration, formatCount } from '@/lib/format'
import { EmptyState } from '@/shared/components/EmptyState'

/**
 * The slowest applies in the window, ranked. Mirrors the Dashboard's org-wide SlowestAppliesCard
 * row idiom (shared-scale horizontal bars), keyed on serial rather than workspace name since we
 * are already inside one workspace.
 *
 * Red carries criticality here. That is the one thing the per-serial chart this replaced showed
 * which nothing else on the tab does, and the reason a duration histogram was the wrong form: a
 * bin holds many versions, so it cannot say "this apply was both slow AND risky".
 */
export function SlowestAppliesList({ applies }: { applies: WorkspaceSlowApply[] }) {
  if (applies.length === 0) {
    return (
      <EmptyState
        icon="▲"
        title="No measured applies in this window"
        subtitle="Runtime is recorded from LOCK to state push. Promotes, restores and resource removals are not measured applies, so they are counted as pushes but never ranked here."
      />
    )
  }

  // The list arrives longest-first, so the first row is the max and sets the shared scale.
  const maxMs = applies[0]!.duration_ms || 1

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {applies.map((a) => {
        const critical = a.critical_count > 0
        const colour = critical ? 'var(--red)' : 'var(--accent)'
        return (
          <div key={`${a.serial}-${a.pushed_at}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--text)' }}>
                serial {a.serial}
              </span>
              <span style={{ fontFamily: 'var(--head)', fontSize: '0.9rem', fontWeight: 700, color: colour }}>
                {formatDuration(a.duration_ms)}
              </span>
            </div>
            <div aria-hidden style={{ height: 4, background: 'var(--bg3)', borderRadius: 2, margin: '5px 0 4px' }}>
              <div style={{ width: `${Math.max((a.duration_ms / maxMs) * 100, 2)}%`, height: '100%', background: colour, borderRadius: 2 }} />
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--text3)' }}>
              {formatDateTime(a.pushed_at)} · {formatCount(a.resource_count)} resources
              {critical && <span style={{ color: 'var(--red)' }}> · critical finding</span>}
            </div>
          </div>
        )
      })}
    </div>
  )
}
